import { useState, useEffect } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { authFetch } from "@/lib/auth";

const CATEGORIES = ["Engine", "Drivetrain", "Brakes", "Electrical", "Cooling", "Body", "Suspension", "Fuel System", "Other"];
const CONDITIONS = [{ v: "new", l: "New" }, { v: "used", l: "Used" }, { v: "refurbished", l: "Refurbished" }];

export default function EditPartDialog({ part, onClose, onSaved }) {
  const [form, setForm] = useState({ ...part });
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm({ ...part }); }, [part]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        category: form.category,
        make: form.make,
        model: form.model,
        year_from: Number(form.year_from),
        year_to: Number(form.year_to),
        engine: form.engine,
        variant: form.variant,
        oem_number: form.oem_number,
        condition: form.condition,
        price_zar: Number(form.price_zar),
        warranty_months: Number(form.warranty_months),
        compatibility_notes: form.compatibility_notes,
        in_stock: form.in_stock,
      };
      const { data } = await api.patch(`/parts/${part.id}`, payload, authFetch());
      toast.success("Part updated");
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="edit-part-dialog" className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 overflow-auto" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white border border-neutral-200 p-6 my-10 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-mono-tech text-[#FF5100]">EDIT LISTING</div>
            <h2 className="font-display text-2xl font-bold mt-1 text-neutral-900">{part.name}</h2>
          </div>
          <button data-testid="edit-close" onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X size={18} /></button>
        </div>

        <form onSubmit={save} className="space-y-3">
          <Inp label="Part name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-name" />
          <Row cols={2}>
            <Sel label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES} testid="edit-category" />
            <Sel label="Condition" value={form.condition} onChange={(v) => setForm({ ...form, condition: v })} options={CONDITIONS.map(c => c.v)} labels={CONDITIONS.map(c => c.l)} testid="edit-condition" />
          </Row>
          <Row cols={2}>
            <Inp label="Make" value={form.make} onChange={(v) => setForm({ ...form, make: v })} testid="edit-make" />
            <Inp label="Model" value={form.model} onChange={(v) => setForm({ ...form, model: v })} testid="edit-model" />
          </Row>
          <Row cols={2}>
            <Inp label="Year from" type="number" value={form.year_from} onChange={(v) => setForm({ ...form, year_from: v })} testid="edit-year-from" />
            <Inp label="Year to" type="number" value={form.year_to} onChange={(v) => setForm({ ...form, year_to: v })} testid="edit-year-to" />
          </Row>
          <Row cols={2}>
            <Inp label="Engine" value={form.engine} onChange={(v) => setForm({ ...form, engine: v })} testid="edit-engine" />
            <Inp label="Variant" value={form.variant || ""} onChange={(v) => setForm({ ...form, variant: v })} testid="edit-variant" />
          </Row>
          <Inp label="OEM #" value={form.oem_number || ""} onChange={(v) => setForm({ ...form, oem_number: v })} testid="edit-oem" mono />
          <Row cols={2}>
            <Inp label="Price (ZAR)" type="number" value={form.price_zar} onChange={(v) => setForm({ ...form, price_zar: v })} testid="edit-price" />
            <Inp label="Warranty (months)" type="number" value={form.warranty_months || 0} onChange={(v) => setForm({ ...form, warranty_months: v })} testid="edit-warranty" />
          </Row>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1.5">Compatibility notes</div>
            <textarea
              data-testid="edit-notes"
              value={form.compatibility_notes || ""}
              onChange={(e) => setForm({ ...form, compatibility_notes: e.target.value })}
              rows={2}
              className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 py-2 text-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="edit-in-stock"
              checked={form.in_stock !== false}
              onChange={(e) => setForm({ ...form, in_stock: e.target.checked })}
              className="accent-[#FF5100]"
            />
            <span>In stock (uncheck to hide from search without deleting)</span>
          </label>
          <button
            type="submit"
            data-testid="edit-save"
            disabled={busy}
            className="w-full px-6 h-11 bg-[#FF5100] hover:bg-[#E64A00] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs transition-colors flex items-center justify-center gap-2"
          >
            {busy ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : "Save Changes"}
          </button>
        </form>
      </div>
    </div>
  );
}

const Row = ({ children, cols = 1 }) => (
  <div className={`grid gap-3 ${cols === 2 ? "grid-cols-2" : "grid-cols-1"}`}>{children}</div>
);

function Inp({ label, value, onChange, type = "text", testid, mono }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1.5">{label}</div>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm ${mono ? "font-mono-tech" : ""}`}
      />
    </label>
  );
}

function Sel({ label, value, onChange, options, labels, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1.5">{label}</div>
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
      >
        {options.map((o, i) => <option key={o} value={o}>{labels?.[i] ?? o}</option>)}
      </select>
    </label>
  );
}
